/**
 * ViewportMenu — the DOM half of the viewport right-click menu (ENG-1050).
 * Mounted in App.jsx (outside the Canvas, where react-dom owns the tree) and
 * driven by viewportMenuStore, which ViewportContextMenu fills from inside the
 * Canvas. See the store's header for why the split is mandatory.
 *
 * The menu knows nothing about what it can do: it renders whatever
 * `viewportCommandsFor` resolved. Adding "Look at" later is a
 * registerViewportCommand call in that feature's own module.
 *
 * An unavailable command is rendered DISABLED with its reason inline — never
 * hidden, and never an empty menu that leaves the user guessing why their click
 * did nothing.
 */
import { useEffect, useRef, useState } from 'react'
import { viewportSize } from '../lib/windowStore'
import { viewportMenuStore } from '../lib/viewportMenuStore'

const MENU_W = 200
/** Grace period before a hovered-open editor closes again. Long enough to cross
 *  the row and reach the flyout, short enough not to feel stuck. */
const HOVER_CLOSE_MS = 160

export default function ViewportMenu() {
  // Which row's editor flyout is open, by command id. Reset whenever the menu
  // itself reopens, so an arrow left open does not greet the next right-click.
  const [editing, setEditing] = useState(null)
  // Hover open/close with a small grace period. Without it, the pointer leaving
  // the arrow for the flyout — or clipping a corner on the way — shuts the thing
  // it is travelling toward.
  const hoverTimer = useRef(null)
  const cancelClose = () => { if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null } }
  const openEditorFor = (id) => { cancelClose(); setEditing(id) }
  const closeEditorSoon = () => {
    cancelClose()
    hoverTimer.current = setTimeout(() => { hoverTimer.current = null; setEditing(null) }, HOVER_CLOSE_MS)
  }
  const [open, setOpen] = useState(() => viewportMenuStore.getState().open)
  const ref = useRef(null)

  // A new right-click starts with every flyout shut, and no pending close from
  // the last one.
  useEffect(() => { cancelClose(); setEditing(null) }, [open])
  useEffect(() => cancelClose, [])
  useEffect(() => viewportMenuStore.subscribe(() => setOpen(viewportMenuStore.getState().open)), [])

  // Dismissal: Escape, a pointer outside the menu, a wheel (the world moved
  // under it), or the window losing focus.
  useEffect(() => {
    if (!open) return
    const close = () => viewportMenuStore.close()
    // Escape closes the FLYOUT first if one is open, then the menu — the usual
    // nested-dismiss order, so a mistyped prompt does not cost the whole menu.
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      setEditing((cur) => { if (cur) return null; close(); return null })
    }
    const onDown = (e) => { if (!ref.current?.contains(e.target)) close() }
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('pointerdown', onDown, true)
    window.addEventListener('wheel', close, { passive: true })
    window.addEventListener('blur', close)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('wheel', close)
      window.removeEventListener('blur', close)
    }
  }, [open])

  if (!open) return null

  /** Close the menu and fire a command. Shared by the row and by any editor
   *  flyout that offers its own action, so the two cannot diverge in how they
   *  handle a rejection or whether they close. */
  const runItem = (item) => {
    viewportMenuStore.close()
    // Async commands report their own progress through their own state
    // (walk-here: the pill). A rejection here would be a bug in the command —
    // surface it rather than leaving an unhandled rejection.
    try {
      Promise.resolve(item.run(open.ctx)).catch(err =>
        console.error(`[viewportMenu] ${item.id} failed:`, err))
    } catch (err) {
      console.error(`[viewportMenu] ${item.id} threw:`, err)
    }
  }

  // The app root carries a CSS zoom for UI scale (uiScale.js). A pointer event's
  // clientX/clientY are unzoomed viewport pixels, but an element positioned
  // `fixed` at left:X inside that root paints at X * zoom — so placing the menu
  // at the raw clientX put it right of the cursor by clientX * (zoom - 1), which
  // is nothing at 100% and grows across the screen at any other scale. Dividing
  // puts the click and the placement in the same space; windowStore.viewportSize
  // is the existing owner of that arithmetic and reports the bounds already
  // divided, so the edge flips below are in that space too.
  const { vw, vh, zoom } = viewportSize()
  const x = open.x / zoom
  const y = open.y / zoom

  // Flip near the viewport edges so the menu is never clipped (Fitts: it stays
  // next to the click, just not off-screen).
  const flipX = x + MENU_W + 8 > vw
  const estH = 12 + open.items.length * 30
  const flipY = y + estH + 8 > vh

  return (
    <div
      ref={ref}
      className="aos-viewport-menu"
      data-testid="viewport-context-menu"
      style={{
        left: flipX ? x - MENU_W : x,
        top: flipY ? Math.max(4, y - estH) : y,
        width: MENU_W,
        transformOrigin: `${flipX ? 'right' : 'left'} ${flipY ? 'bottom' : 'top'}`,
      }}
      onContextMenu={e => e.preventDefault()}
    >
      <style>{`
        .aos-viewport-menu {
          position: fixed; z-index: 10001; padding: 4px;
          background: var(--m-panel);
          border: 1px solid var(--m-line);
          border-radius: var(--m-radius-sm);
          box-shadow: var(--m-shadow-card);
          font-family: var(--m-font); font-size: 12px; color: var(--m-fg);
          /* Grows out of the click point (Law of Uniform Connectedness): the menu
             reads as belonging to the spot the user hit, not as a panel that
             appeared. 120ms keeps it under the Doherty threshold. */
          animation: aos-vpmenu-in 120ms var(--m-ease) both;
        }
        @keyframes aos-vpmenu-in {
          from { opacity: 0; transform: scale(.94) }
          to   { opacity: 1; transform: scale(1) }
        }
        .aos-viewport-menu button {
          display: block; width: 100%; text-align: left;
          padding: 6px 10px; border: 0; border-radius: 3px;
          background: transparent; color: var(--m-fg);
          font: inherit; cursor: pointer;
          transition: background 90ms var(--m-ease), color 90ms var(--m-ease);
        }
        .aos-viewport-menu button:hover:not(:disabled),
        .aos-viewport-menu button:focus-visible:not(:disabled) {
          background: var(--m-accent-soft); color: var(--m-accent-ink); outline: none;
        }
        .aos-viewport-menu button:disabled { cursor: default; color: var(--m-fg-3) }
        .aos-viewport-menu .aos-vpmenu-row { position: relative; display: flex; align-items: stretch; }
        .aos-viewport-menu .aos-vpmenu-row > button:first-child { flex: 1; }
        .aos-viewport-menu .aos-vpmenu-disclose {
          width: 22px; flex: 0 0 22px; padding: 0; text-align: center;
          color: var(--m-fg-3); font-size: 10px;
        }
        .aos-viewport-menu .aos-vpmenu-disclose[aria-expanded="true"] { color: var(--m-fg); }
        .aos-viewport-menu .aos-vpmenu-flyout {
          position: absolute; top: 0; z-index: 1;
          margin: 0 4px; padding: 6px 8px 8px;
          background: var(--m-panel); border: 1px solid var(--m-line);
          border-radius: var(--m-radius); box-shadow: var(--m-shadow-card);
        }
        .aos-viewport-menu .aos-vpmenu-badge {
          margin-left: 5px; color: var(--m-accent); font-size: 10px; vertical-align: 1px;
        }
        .aos-viewport-menu .aos-vpmenu-reason {
          display: block; margin-top: 2px;
          font-family: var(--m-mono); font-size: 10px; line-height: 1.3;
          color: var(--m-warn); white-space: normal;
        }
        @media (prefers-reduced-motion: reduce) {
          .aos-viewport-menu { animation: none }
          .aos-viewport-menu button { transition: none }
        }
      `}</style>
      {open.items.length === 0 && (
        <div style={{ padding: '6px 10px', color: 'var(--m-fg-3)' }}>No actions here</div>
      )}
      {open.items.map(item => (
        <div
          key={item.id}
          className="aos-vpmenu-row"
          onPointerEnter={cancelClose}
          // Fires only when the pointer leaves the row AND every descendant —
          // the flyout is a descendant even though it paints outside the row's
          // box, so travelling into it does not count as leaving.
          onPointerLeave={item.editor ? closeEditorSoon : undefined}
        >
        <button
          type="button"
          data-command={item.id}
          disabled={!item.enabled}
          title={item.reason ?? undefined}
          onClick={() => runItem(item)}
          // Moving onto a different row's label dismisses the open flyout — it
          // belongs to the row the pointer has left.
          onPointerEnter={() => { if (editing && editing !== item.id) closeEditorSoon() }}
        >
          {item.label}
          {item.badge && (
            <span className="aos-vpmenu-badge" title={item.badgeTitle ?? undefined}>{item.badge}</span>
          )}
          {!item.enabled && item.reason && (
            <span className="aos-vpmenu-reason">{item.reason}</span>
          )}
        </button>
        {item.editor && (
          <button
            type="button"
            className="aos-vpmenu-disclose"
            data-testid={`viewport-menu-editor-${item.id}`}
            aria-expanded={editing === item.id}
            aria-label={`${item.label} options`}
            title={`${item.label} options`}
            // The row's own click RUNS the command and closes the menu, so the
            // arrow must not bubble into it.
            onPointerEnter={() => openEditorFor(item.id)}
            // Click still toggles: touch has no hover at all, and a keyboard
            // user reaches this with Enter. Hover is the primary path, not the
            // only one.
            onClick={(e) => { e.stopPropagation(); cancelClose(); setEditing(editing === item.id ? null : item.id) }}
            onFocus={() => openEditorFor(item.id)}
          >{editing === item.id ? '▾' : '▸'}</button>
        )}
        {item.editor && editing === item.id && (
          // Inside the menu's own subtree, so the outside-pointerdown close
          // ignores clicks in here and a field can be typed in.
          <div className="aos-vpmenu-flyout" style={{ [flipX ? 'right' : 'left']: '100%' }}>
            <item.editor
              ctx={open.ctx}
              // So an editor can offer its own primary action — adjust the
              // inputs and fire, without closing the flyout to reach the row
              // and losing the point that was right-clicked.
              run={() => runItem(item)}
              enabled={item.enabled}
              reason={item.reason}
            />
          </div>
        )}
        </div>
      ))}
    </div>
  )
}
